import { useCallback, useEffect, useRef } from "react";

const ANNOUNCEMENT_PLAYBACK_TIMEOUT_MS = 4500;
const SPEECH_FALLBACK_TIMEOUT_MS = 2500;

const getAudioContextCtor = () => window.AudioContext || null;

const AUCTION_AUDIO_CLIPS = {
  "opening-prompt": {
    src: "/assets/audio/place-your-opening-bid.mp3",
    fallbackText: "Place your opening bid",
  },
  "going-once": {
    src: "/assets/audio/going-once.mp3",
    fallbackText: "Going once",
  },
  "going-twice": {
    src: "/assets/audio/going-twice.mp3",
    fallbackText: "Going twice",
  },
  sold: {
    src: "/assets/audio/sold.mp3",
    fallbackText: "Sold",
  },
};

/**
 * useAuctionAudio — manages AudioContext, clip preloading, and playback.
 *
 * @param {Function} pushDebugEvent — logging callback from useDebugLog.
 *
 * Returns {
 *   playAuctionClip(clipKey, options?),
 *   stopAuctionClip(),
 *   playBidDing(),
 *   unlockAudioContext(),
 *   resetClipAvailability(),
 * }
 */
export default function useAuctionAudio(pushDebugEvent) {
  const audioContextRef = useRef(null);
  const audioBuffersRef = useRef({});
  const activeAuctionPlaybackRef = useRef(null);
  const auctionAudioAvailabilityRef = useRef({});

  // ── AudioContext init + clip preloading ──────────────────────────
  useEffect(() => {
    const Ctor = getAudioContextCtor();
    if (!Ctor) {
      pushDebugEvent("AudioContext unavailable");
      return undefined;
    }

    let ctx;
    try {
      ctx = new Ctor();
    } catch (err) {
      pushDebugEvent(
        "AudioContext construction failed",
        String(err && err.message ? err.message : err)
      );
      return undefined;
    }
    audioContextRef.current = ctx;

    auctionAudioAvailabilityRef.current = Object.fromEntries(
      Object.keys(AUCTION_AUDIO_CLIPS).map(key => [key, true])
    );

    let cancelled = false;

    Object.entries(AUCTION_AUDIO_CLIPS).forEach(([key, clip]) => {
      fetch(clip.src)
        .then(response => {
          if (!response.ok) {
            throw new Error(`fetch ${response.status}`);
          }
          return response.arrayBuffer();
        })
        .then(arrayBuffer => ctx.decodeAudioData(arrayBuffer))
        .then(audioBuffer => {
          if (cancelled) return;
          audioBuffersRef.current[key] = audioBuffer;
          pushDebugEvent("Audio clip loaded", key);
        })
        .catch(err => {
          if (cancelled) return;
          auctionAudioAvailabilityRef.current[key] = false;
          pushDebugEvent(
            "Audio clip load failed",
            `${key}: ${err && err.message ? err.message : err}`
          );
        });
    });

    return () => {
      cancelled = true;
      if (
        activeAuctionPlaybackRef.current &&
        typeof activeAuctionPlaybackRef.current.cleanup === "function"
      ) {
        try {
          activeAuctionPlaybackRef.current.cleanup();
        } catch {}
      }
      activeAuctionPlaybackRef.current = null;
      audioBuffersRef.current = {};
      auctionAudioAvailabilityRef.current = {};
      if (ctx && typeof ctx.close === "function") {
        try {
          ctx.close();
        } catch {}
      }
      audioContextRef.current = null;
    };
  }, [pushDebugEvent]);

  // ── Low-level helpers ───────────────────────────────────────────

  const stopAuctionClip = useCallback(() => {
    const activePlayback = activeAuctionPlaybackRef.current;
    if (!activePlayback) return;
    if (typeof activePlayback.cleanup === "function") {
      activePlayback.cleanup();
    }
    activeAuctionPlaybackRef.current = null;
  }, []);

  const playSpeechFallback = useCallback(
    text =>
      new Promise(resolve => {
        if (
          !window.speechSynthesis ||
          typeof SpeechSynthesisUtterance === "undefined"
        ) {
          pushDebugEvent("Speech fallback unavailable", text);
          resolve();
          return;
        }

        let settled = false;
        const timeoutId = setTimeout(() => {
          if (settled) return;
          settled = true;
          pushDebugEvent("Speech fallback timed out", text);
          resolve();
        }, SPEECH_FALLBACK_TIMEOUT_MS);

        const finish = status => {
          if (settled) return;
          settled = true;
          clearTimeout(timeoutId);
          pushDebugEvent("Speech fallback finished", `${text}: ${status}`);
          resolve();
        };

        const utterance = new SpeechSynthesisUtterance(text);
        utterance.onend = () => finish("ended");
        utterance.onerror = () => finish("error");
        pushDebugEvent("Speech fallback started", text);
        window.speechSynthesis.speak(utterance);
      }),
    [pushDebugEvent]
  );

  const playBidDing = useCallback(() => {
    const ctx = audioContextRef.current;
    if (!ctx || ctx.state !== "running") return;

    try {
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();

      oscillator.type = "sine";
      oscillator.frequency.setValueAtTime(1200, ctx.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(
        800,
        ctx.currentTime + 0.15
      );

      gain.gain.setValueAtTime(0.18, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25);

      oscillator.connect(gain);
      gain.connect(ctx.destination);

      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.25);
    } catch {
      // Non-critical — silently ignore if synthesis fails.
    }
  }, []);

  // ── Main playback ───────────────────────────────────────────────

  const playAuctionClip = useCallback(
    async (clipKey, options = {}) => {
      const { useSpeechFallback = true } = options;
      const clipConfig = AUCTION_AUDIO_CLIPS[clipKey];
      const ctx = audioContextRef.current;
      const buffer = audioBuffersRef.current[clipKey];
      const isClipAvailable =
        auctionAudioAvailabilityRef.current[clipKey] !== false;

      if (!clipConfig) return;

      const fallbackIfEnabled = async () => {
        if (useSpeechFallback) {
          await playSpeechFallback(clipConfig.fallbackText);
        }
      };

      if (!ctx || !buffer || !isClipAvailable) {
        pushDebugEvent("Audio clip unavailable", clipKey);
        await fallbackIfEnabled();
        return;
      }

      if (ctx.state === "suspended") {
        try {
          await ctx.resume();
        } catch {
          auctionAudioAvailabilityRef.current[clipKey] = false;
          pushDebugEvent("AudioContext resume failed", clipKey);
          await fallbackIfEnabled();
          return;
        }
      }

      if (ctx.state !== "running") {
        auctionAudioAvailabilityRef.current[clipKey] = false;
        pushDebugEvent("AudioContext not running", `${clipKey}: ${ctx.state}`);
        await fallbackIfEnabled();
        return;
      }

      stopAuctionClip();
      pushDebugEvent("Audio clip play requested", clipKey);

      await new Promise(resolve => {
        let settled = false;
        let source;

        const finalize = () => {
          if (settled) return;
          settled = true;
          clearTimeout(timeoutId);
          if (source) {
            try { source.onended = null; } catch {}
            try { source.stop(); } catch {}
            try { source.disconnect(); } catch {}
          }
          if (activeAuctionPlaybackRef.current?.clipKey === clipKey) {
            activeAuctionPlaybackRef.current = null;
          }
          resolve();
        };

        const timeoutId = setTimeout(() => {
          auctionAudioAvailabilityRef.current[clipKey] = false;
          pushDebugEvent("Audio clip timed out", clipKey);
          finalize();
        }, ANNOUNCEMENT_PLAYBACK_TIMEOUT_MS);

        try {
          source = ctx.createBufferSource();
          source.buffer = buffer;
          source.connect(ctx.destination);
          source.onended = () => {
            pushDebugEvent("Audio clip ended", clipKey);
            finalize();
          };

          activeAuctionPlaybackRef.current = { clipKey, cleanup: finalize };
          source.start(0);
          pushDebugEvent("Audio clip playback confirmed", clipKey);
        } catch {
          auctionAudioAvailabilityRef.current[clipKey] = false;
          pushDebugEvent("Audio clip play rejected", clipKey);
          finalize();
        }
      });

      if (
        useSpeechFallback &&
        auctionAudioAvailabilityRef.current[clipKey] === false
      ) {
        await playSpeechFallback(clipConfig.fallbackText);
      }
    },
    [playSpeechFallback, pushDebugEvent, stopAuctionClip]
  );

  // ── User-gesture helpers ────────────────────────────────────────

  const unlockAudioContext = useCallback(() => {
    const ctx = audioContextRef.current;
    if (!ctx) return;

    if (ctx.state === "suspended") {
      const result = ctx.resume();
      if (result && typeof result.then === "function") {
        result
          .then(() => pushDebugEvent("AudioContext resumed"))
          .catch(() => pushDebugEvent("AudioContext resume failed"));
      }
    }
  }, [pushDebugEvent]);

  // Give every loaded clip a fresh shot at playing back so a single flake
  // in a prior auction doesn't permanently force speech-synthesis fallback.
  const resetClipAvailability = useCallback(() => {
    Object.keys(AUCTION_AUDIO_CLIPS).forEach(key => {
      if (audioBuffersRef.current[key]) {
        auctionAudioAvailabilityRef.current[key] = true;
      }
    });
  }, []);

  return {
    playAuctionClip,
    stopAuctionClip,
    playSpeechFallback,
    playBidDing,
    unlockAudioContext,
    resetClipAvailability,
  };
}
